import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Tabs,
  TabsProps,
  message,
  theme,
} from "antd";
import {
  useCreatePlanMutation,
  useUpdatePlanMutation,
  useGetPlanQuery,
  GetAllPlansDocument,
} from "../graphql/generated/hooks";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import RobinPage from "../components/RobinPage";
import { useEffect } from "react";
import {
  MinusCircleOutlined,
  CarOutlined,
  ThunderboltOutlined,
  ShoppingOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  FireOutlined,
  RocketOutlined,
} from "@ant-design/icons";

import { ScheduledType } from "../graphql/generated/schemas";
import { parseArabicNumbers } from "../common/util";
import { MAX_PLAN_CONDITIONS } from "../common/constants";
import FormListAddButton from "../common/FormListAddButton";

const DEFAULT_DELIVERY_CONDITION = {
  scheduledType: ScheduledType.Normal,
};

function PlanMutate() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const params = useParams();
  const { token } = theme.useToken();

  const editMode = !!params.id;
  const planId = params.id ? parseInt(params.id, 10) : 0;

  const { data, loading: fetching } = useGetPlanQuery({
    variables: { id: planId },
    skip: !editMode,
    fetchPolicy: "network-only",
  });

  const [createPlan, { loading: creating }] = useCreatePlanMutation();
  const [updatePlan, { loading: updating }] = useUpdatePlanMutation();

  const plan = data?.getPlan;
  const hasValue = (value: unknown) => value !== undefined && value !== null;
  const scheduledTypeOptions = [
    {
      label: t("label.delivery.items.scheduledType.normal"),
      value: ScheduledType.Normal,
    },
    {
      label: t("label.delivery.items.scheduledType.urgent"),
      value: ScheduledType.Urgent,
    },
    {
      label: t("label.delivery.items.scheduledType.scheduled"),
      value: ScheduledType.Scheduled,
    },
  ];

  useEffect(() => {
    if (plan) {
      form.setFieldsValue({
        name: plan.name,
        commissionRate: plan.commissionRate,
        minCommissionAmount: plan.minCommissionAmount,
        deliveryPlan: {
          defaultDeliveryFee: plan.deliveryPlan?.defaultDeliveryFee,
          items:
            plan.deliveryPlan?.items?.map(
              ({
                minimumCartAmount,
                partnerCoverageAmount,
                afterDiscountDeliveryFee,
                scheduledType,
              }) => ({
                minimumCartAmount,
                partnerCoverageAmount,
                afterDiscountDeliveryFee,
                scheduledType,
              }),
            ) ?? [],
        },
        individualOrderDiscounts: plan.individualOrderDiscounts.map(
          ({
            discountRate,
            minimumCartAmount,
            partnerCoverageRate,
            maxDiscountAmount,
          }) => ({
            discountRate,
            minimumCartAmount,
            partnerCoverageType: "RATE",
            partnerCoverageValue: partnerCoverageRate,
            maxDiscountAmount,
          }),
        ),
        scheduledIndividualOrderDiscounts:
          plan.scheduledIndividualOrderDiscounts?.map(
            ({
              discountRate,
              minimumCartAmount,
              partnerCoverageRate,
              maxDiscountAmount,
            }) => ({
              discountRate,
              minimumCartAmount,
              partnerCoverageType: "RATE",
              partnerCoverageValue: partnerCoverageRate,
              maxDiscountAmount,
            }),
          ) || [],
        urgentIndividualOrderDiscounts:
          plan.urgentIndividualOrderDiscounts?.map(
            ({
              discountRate,
              minimumCartAmount,
              partnerCoverageRate,
              maxDiscountAmount,
            }) => ({
              discountRate,
              minimumCartAmount,
              partnerCoverageType: "RATE",
              partnerCoverageValue: partnerCoverageRate,
              maxDiscountAmount,
            }),
          ) || [],
        groupOrderDiscounts: plan.groupOrderDiscounts.map(
          ({
            discountRate,
            minimumCartAmount,
            partnerCoverageRate,
            numberOfMembers,
            maxDiscountAmount,
          }) => ({
            discountRate,
            minimumCartAmount,
            partnerCoverageType: "RATE",
            partnerCoverageValue: partnerCoverageRate,
            numberOfMembers,
            maxDiscountAmount,
          }),
        ),
        groupDeliveryDiscounts: plan.groupDeliveryDiscounts.map(
          ({
            discountRate,
            minimumCartAmount,
            partnerCoverageRate,
            numberOfMembers,
            maxDiscountAmount,
          }) => ({
            discountRate,
            minimumCartAmount,
            partnerCoverageType: "RATE",
            partnerCoverageValue: partnerCoverageRate,
            numberOfMembers,
            maxDiscountAmount,
          }),
        ),
        flashSaleDiscounts: plan.flashSaleDiscounts.map(
          ({
            discountRate,
            minimumCartAmount,
            partnerCoverageRate,
            numberOfMembers,
            maxDiscountAmount,
          }) => ({
            discountRate,
            minimumCartAmount,
            partnerCoverageType: "RATE",
            partnerCoverageValue: partnerCoverageRate,
            numberOfMembers,
            maxDiscountAmount,
          }),
        ),
      });
    }
  }, [plan, form]);

  const onFinish = async (_values: any) => {
    // Read from the form store to include fields that may not be mounted
    // (e.g. lists inside inactive tabs).
    const values = form.getFieldsValue(true);
    const mutationVariables = {
      name: values.name,
      commissionRate: values.commissionRate,
      minCommissionAmount: values.minCommissionAmount,
      deliveryPlan: {
        defaultDeliveryFee: values.deliveryPlan?.defaultDeliveryFee,
        items: (values.deliveryPlan?.items ?? []).map((item: any) => ({
          minimumCartAmount: item.minimumCartAmount,
          partnerCoverageAmount: item.partnerCoverageAmount,
          scheduledType: item.scheduledType,
          ...(hasValue(item.afterDiscountDeliveryFee)
            ? { afterDiscountDeliveryFee: item.afterDiscountDeliveryFee }
            : {}),
        })),
      },
      individualOrderDiscounts: (values.individualOrderDiscounts ?? []).map(
        (row: any) => ({
          minimumCartAmount: row.minimumCartAmount,
          discountRate: row.discountRate,
          partnerCoverageType: row.partnerCoverageType ?? "RATE",
          partnerCoverageRate: row.partnerCoverageValue,
          ...(hasValue(row.maxDiscountAmount)
            ? { maxDiscountAmount: row.maxDiscountAmount }
            : {}),
        }),
      ),
      scheduledIndividualOrderDiscounts: (
        values.scheduledIndividualOrderDiscounts ?? []
      ).map((row: any) => ({
        minimumCartAmount: row.minimumCartAmount,
        discountRate: row.discountRate,
        partnerCoverageType: row.partnerCoverageType ?? "RATE",
        partnerCoverageRate: row.partnerCoverageValue,
        ...(hasValue(row.maxDiscountAmount)
          ? { maxDiscountAmount: row.maxDiscountAmount }
          : {}),
      })),
      urgentIndividualOrderDiscounts: (
        values.urgentIndividualOrderDiscounts ?? []
      ).map((row: any) => ({
        minimumCartAmount: row.minimumCartAmount,
        discountRate: row.discountRate,
        partnerCoverageType: row.partnerCoverageType ?? "RATE",
        partnerCoverageRate: row.partnerCoverageValue,
        ...(hasValue(row.maxDiscountAmount)
          ? { maxDiscountAmount: row.maxDiscountAmount }
          : {}),
      })),
      groupDeliveryDiscounts: (values.groupDeliveryDiscounts ?? []).map(
        (row: any) => ({
          minimumCartAmount: row.minimumCartAmount,
          discountRate: row.discountRate,
          partnerCoverageType: row.partnerCoverageType ?? "RATE",
          partnerCoverageRate: row.partnerCoverageValue,
          ...(hasValue(row.maxDiscountAmount)
            ? { maxDiscountAmount: row.maxDiscountAmount }
            : {}),
          numberOfMembers: row.numberOfMembers,
        }),
      ),
      groupOrderDiscounts: (values.groupOrderDiscounts ?? []).map(
        (row: any) => ({
          minimumCartAmount: row.minimumCartAmount,
          discountRate: row.discountRate,
          partnerCoverageType: row.partnerCoverageType ?? "RATE",
          partnerCoverageRate: row.partnerCoverageValue,
          ...(hasValue(row.maxDiscountAmount)
            ? { maxDiscountAmount: row.maxDiscountAmount }
            : {}),
          numberOfMembers: row.numberOfMembers,
        }),
      ),
      flashSaleDiscounts: (values.flashSaleDiscounts ?? []).map((row: any) => ({
        minimumCartAmount: row.minimumCartAmount,
        discountRate: row.discountRate,
        partnerCoverageType: row.partnerCoverageType ?? "RATE",
        partnerCoverageRate: row.partnerCoverageValue,
        ...(hasValue(row.maxDiscountAmount)
          ? { maxDiscountAmount: row.maxDiscountAmount }
          : {}),
        numberOfMembers: row.numberOfMembers,
      })),
    };

    if (editMode)
      await updatePlan({
        variables: {
          id: planId,
          ...mutationVariables,
        },
        refetchQueries: [{ query: GetAllPlansDocument }],
        awaitRefetchQueries: true,
      })
        .then(() => {
          messageApi.success(t("message.success.data_updated"));
          navigate(-1);
        })
        .catch((error) => {
          messageApi.error(t("message.error.data_update_failed"));
          console.error(error);
        });
    else
      await createPlan({
        variables: mutationVariables,
        refetchQueries: [{ query: GetAllPlansDocument }],
        awaitRefetchQueries: true,
      })
        .then(() => {
          messageApi.success(t("message.success.data_created"));
          navigate(-1);
        })
        .catch((error) => {
          messageApi.error(t("message.error.data_create_failed"));
          console.error(error);
        });
  };

  const renderItemFields = (
    namePrefix: (string | number)[],
    compact = false,
    listNamePath: (string | number)[],
  ) => (
    <>
      <Col md={4} xs={24}>
        <Form.Item
          name={[...namePrefix, "minimumCartAmount"]}
          label={compact ? "Min Cart" : t("label.discount.min_cart_amount")}
          dependencies={[listNamePath]}
          rules={[
            {
              required: true,
              message: t("message.validation.required.minimum_cart_amount"),
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (value === undefined || value === null)
                  return Promise.resolve();
                const items = getFieldValue(listNamePath) || [];
                const count = items.filter(
                  (item: any) => item?.minimumCartAmount === value,
                ).length;
                if (count > 1)
                  return Promise.reject(
                    new Error(t("message.error.duplicate_condition")),
                  );
                return Promise.resolve();
              },
            }),
          ]}
        >
          <InputNumber
            min={0}
            precision={2}
            addonAfter={t("label.currency")}
            style={{ width: "100%" }}
            parser={parseArabicNumbers}
          />
        </Form.Item>
      </Col>
      <Col md={4} xs={24}>
        <Form.Item
          name={[...namePrefix, "discountRate"]}
          label={t("label.discount.discount")}
          rules={[
            {
              required: true,
              message: t("message.validation.required.discount"),
            },
          ]}
        >
          <InputNumber
            min={0}
            max={100}
            precision={2}
            addonAfter={"%"}
            style={{ width: "100%" }}
            parser={parseArabicNumbers}
          />
        </Form.Item>
      </Col>
      <Col md={3} xs={24}>
        <Form.Item
          name={[...namePrefix, "partnerCoverageType"]}
          label={t("label.discount.partner_coverage_type")}
          initialValue="RATE"
        >
          <Select
            options={[
              { label: t("label.discount.percentage"), value: "RATE" },
              { label: t("label.discount.amount"), value: "AMOUNT" },
            ]}
          />
        </Form.Item>
      </Col>
      <Col md={4} xs={24}>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const coverageType = getFieldValue([
              ...listNamePath,
              namePrefix[0],
              "partnerCoverageType",
            ]);
            const isAmount = coverageType === "AMOUNT";

            return (
              <Form.Item
                name={[...namePrefix, "partnerCoverageValue"]}
                label={
                  compact
                    ? isAmount
                      ? "Partner Amt"
                      : "Partner %"
                    : t("label.discount.partner_coverage")
                }
                rules={[
                  {
                    required: true,
                    message: t("message.validation.required.partner_coverage"),
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  max={isAmount ? undefined : 100}
                  precision={2}
                  addonAfter={isAmount ? t("label.currency") : "%"}
                  style={{ width: "100%" }}
                  parser={parseArabicNumbers}
                />
              </Form.Item>
            );
          }}
        </Form.Item>
      </Col>
      <Col md={5} xs={24}>
        <Form.Item
          name={[...namePrefix, "maxDiscountAmount"]}
          label={
            compact ? "Max Disc." : t("label.discount.max_discount_amount")
          }
        >
          <InputNumber
            min={0}
            precision={2}
            addonAfter={t("label.currency")}
            placeholder="Optional"
            style={{ width: "100%" }}
            parser={parseArabicNumbers}
          />
        </Form.Item>
      </Col>
    </>
  );

  const renderExtendedItemFields = (
    namePrefix: (string | number)[],
    listNamePath: (string | number)[],
  ) => (
    <>
      {renderItemFields(namePrefix, true, listNamePath)}
      <Col md={3} xs={24}>
        <Form.Item
          name={[...namePrefix, "numberOfMembers"]}
          label={t("label.discount.members")}
          rules={[
            {
              required: true,
              message: t("message.validation.required.number_of_members"),
            },
          ]}
        >
          <InputNumber
            min={1}
            precision={0}
            style={{ width: "100%" }}
            parser={parseArabicNumbers}
          />
        </Form.Item>
      </Col>
    </>
  );

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: (
        <span>
          <CarOutlined style={{ marginRight: 8 }} />
          {t("label.delivery.plan")}
        </span>
      ),
      children: (
        <Card
          title={
            <span>
              <CarOutlined style={{ marginRight: 8 }} />
              {t("label.delivery.plan")}
            </span>
          }
          variant="borderless"
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
          }}
          styles={{
            header: {
              color: "white",
              borderBottom: "1px solid rgba(255,255,255,0.2)",
            },
            body: {
              background: token.colorBgContainer,
              borderRadius: "0 0 8px 8px",
            },
          }}
        >
          <Row gutter={[16, 16]}>
            <Col md={12} xs={24}>
              <Form.Item
                name={["deliveryPlan", "defaultDeliveryFee"]}
                label={t("label.delivery.default_fee")}
                rules={[
                  {
                    required: true,
                    message: t(
                      "message.validation.required.default_delivery_fee",
                    ),
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  max={100000}
                  precision={2}
                  addonAfter={t("label.currency")}
                  style={{ width: "100%" }}
                  parser={parseArabicNumbers}
                />
              </Form.Item>
            </Col>
          </Row>
          <Divider orientation="left">{t("label.delivery.conditions")}</Divider>
          <Form.List
            name={["deliveryPlan", "items"]}
            initialValue={[DEFAULT_DELIVERY_CONDITION]}
          >
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name }) => (
                  <Row
                    key={key}
                    gutter={[16, 16]}
                    style={{
                      width: "100%",
                      paddingBottom: 16,
                      borderBottom: "1px solid #f0f0f0",
                      marginBottom: 16,
                    }}
                  >
                    <Col md={4} xs={24}>
                      <Form.Item
                        name={[name, "minimumCartAmount"]}
                        label={t("label.discount.min_cart_amount")}
                        dependencies={[["deliveryPlan", "items"]]}
                        rules={[
                          {
                            required: true,
                            message: t(
                              "message.validation.required.minimum_cart_amount",
                            ),
                          },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (value === undefined || value === null)
                                return Promise.resolve();
                              const items =
                                getFieldValue(["deliveryPlan", "items"]) || [];
                              const currentScheduledType = getFieldValue([
                                "deliveryPlan",
                                "items",
                                name,
                                "scheduledType",
                              ]);
                              const count = items.filter(
                                (item: any) =>
                                  item?.minimumCartAmount === value &&
                                  item?.scheduledType === currentScheduledType,
                              ).length;
                              if (count > 1)
                                return Promise.reject(
                                  new Error(
                                    t("message.error.duplicate_condition"),
                                  ),
                                );
                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <InputNumber
                          min={0}
                          precision={2}
                          addonAfter={t("label.currency")}
                          style={{ width: "100%" }}
                          parser={parseArabicNumbers}
                        />
                      </Form.Item>
                    </Col>
                    <Col md={4} xs={24}>
                      <Form.Item
                        name={[name, "partnerCoverageAmount"]}
                        label={t("label.discount.partner_coverage_amount")}
                        dependencies={[["deliveryPlan", "defaultDeliveryFee"]]}
                        rules={[
                          {
                            required: true,
                            message: t(
                              "message.validation.required.partner_coverage",
                            ),
                          },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (value === undefined || value === null)
                                return Promise.resolve();

                              const defaultFee = getFieldValue([
                                "deliveryPlan",
                                "defaultDeliveryFee",
                              ]);

                              if (
                                defaultFee !== undefined &&
                                value > defaultFee
                              ) {
                                return Promise.reject(
                                  new Error(
                                    t(
                                      "message.error.partner_coverage_exceeds_default_delivery_fee",
                                    ),
                                  ),
                                );
                              }

                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <InputNumber
                          min={0}
                          precision={2}
                          addonAfter={t("label.currency")}
                          style={{ width: "100%" }}
                          parser={parseArabicNumbers}
                        />
                      </Form.Item>
                    </Col>
                    <Col md={4} xs={24}>
                      <Form.Item
                        name={[name, "scheduledType"]}
                        label={t("label.delivery.scheduledType")}
                        dependencies={[["deliveryPlan", "items"]]}
                        rules={[
                          {
                            required: true,
                            message: t(
                              "message.validation.required.scheduledType",
                            ),
                          },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (value === undefined || value === null) {
                                return Promise.resolve();
                              }

                              const items =
                                getFieldValue(["deliveryPlan", "items"]) || [];
                              const currentMinimumCartAmount = getFieldValue([
                                "deliveryPlan",
                                "items",
                                name,
                                "minimumCartAmount",
                              ]);

                              if (
                                currentMinimumCartAmount === undefined ||
                                currentMinimumCartAmount === null
                              ) {
                                return Promise.resolve();
                              }

                              const count = items.filter(
                                (item: any) =>
                                  item?.scheduledType === value &&
                                  item?.minimumCartAmount ===
                                    currentMinimumCartAmount,
                              ).length;

                              if (count > 1) {
                                return Promise.reject(
                                  new Error(
                                    t("message.error.duplicate_condition"),
                                  ),
                                );
                              }

                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <Select options={scheduledTypeOptions} />
                      </Form.Item>
                    </Col>
                    <Col md={4} xs={24}>
                      <Form.Item
                        name={[name, "afterDiscountDeliveryFee"]}
                        label={t("label.delivery.after_discount_fee")}
                        dependencies={[["deliveryPlan", "defaultDeliveryFee"]]}
                        rules={[
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (value === undefined || value === null)
                                return Promise.resolve();
                              const defaultFee = getFieldValue([
                                "deliveryPlan",
                                "defaultDeliveryFee",
                              ]);
                              if (
                                defaultFee !== undefined &&
                                value >= defaultFee
                              ) {
                                return Promise.reject(
                                  new Error(
                                    t("message.error.discount_exceeds_default"),
                                  ),
                                );
                              }
                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <InputNumber
                          min={0}
                          max={100000}
                          precision={2}
                          addonAfter={t("label.currency")}
                          style={{ width: "100%" }}
                          parser={parseArabicNumbers}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      md={4}
                      xs={24}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        paddingTop: 8,
                      }}
                    >
                      <MinusCircleOutlined
                        onClick={() => remove(name)}
                        style={{ color: "#ff4d4f", fontSize: 16 }}
                      />
                    </Col>
                  </Row>
                ))}
                <FormListAddButton
                  fieldsLength={fields.length}
                  maxLimit={MAX_PLAN_CONDITIONS}
                  add={add}
                  buttonLabel={t("button.add_condition")}
                />
              </>
            )}
          </Form.List>
        </Card>
      ),
    },
    ...[
      {
        name: "urgentIndividualOrderDiscounts",
        label: t("label.urgentIndividualOrderDiscounts"),
        icon: <ThunderboltOutlined />,
        renderFields: (prefix: (string | number)[], listName: string) =>
          renderItemFields(prefix, false, [listName]),
        gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      },
      {
        name: "individualOrderDiscounts",
        label: t("label.individual_order_discounts_ScheduledandOrdinary"),
        icon: <ShoppingOutlined />,
        renderFields: (prefix: (string | number)[], listName: string) =>
          renderItemFields(prefix, false, [listName]),
        gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      },
      {
        name: "scheduledIndividualOrderDiscounts",
        label: t("label.scheduled_individual_order_discounts"),
        icon: <ClockCircleOutlined />,
        renderFields: (prefix: (string | number)[], listName: string) =>
          renderItemFields(prefix, false, [listName]),
        gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
      },
      {
        name: "groupDeliveryDiscounts",
        label: t("label.group_discounts"),
        icon: <RocketOutlined />,
        renderFields: (prefix: (string | number)[], listName: string) =>
          renderExtendedItemFields(prefix, [listName]),
        gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
      },
      {
        name: "groupOrderDiscounts",
        label: t("label.group_order"),
        icon: <TeamOutlined />,
        renderFields: (prefix: (string | number)[], listName: string) =>
          renderExtendedItemFields(prefix, [listName]),
        gradient: "linear-gradient(135deg, #30cfd0 0%, #330867 100%)",
      },
      {
        name: "flashSaleDiscounts",
        label: t("label.flash_sale"),
        icon: <FireOutlined />,
        renderFields: (prefix: (string | number)[], listName: string) =>
          renderExtendedItemFields(prefix, [listName]),
        gradient: "linear-gradient(135deg, #ff9a56 0%, #ff6a88 100%)",
      },
    ].map(({ name, label, icon, renderFields, gradient }, index) => ({
      key: `${index + 2}`,
      label: (
        <span>
          {icon}
          <span style={{ marginLeft: 8 }}>{label}</span>
        </span>
      ),
      children: (
        <Card
          title={
            <span>
              {icon}
              <span style={{ marginLeft: 8 }}>{label}</span>
            </span>
          }
          key={name}
          variant="borderless"
          style={{ background: gradient, color: "white" }}
          styles={{
            header: {
              color: "white",
              borderBottom: "1px solid rgba(255,255,255,0.2)",
            },
            body: {
              background: token.colorBgContainer,
              borderRadius: "0 0 8px 8px",
            },
          }}
        >
          <Form.List name={name}>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name: fieldName }) => (
                  <Row
                    key={key}
                    gutter={[16, 16]}
                    style={{
                      width: "100%",
                      paddingBottom: 16,
                      borderBottom: "1px solid #f0f0f0",
                      marginBottom: 16,
                    }}
                  >
                    {renderFields([fieldName], name)}
                    <Col
                      md={1}
                      xs={24}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        paddingTop: 8,
                      }}
                    >
                      <MinusCircleOutlined
                        onClick={() => remove(fieldName)}
                        style={{ color: "#ff4d4f", fontSize: 16 }}
                      />
                    </Col>
                  </Row>
                ))}
                <FormListAddButton
                  fieldsLength={fields.length}
                  maxLimit={MAX_PLAN_CONDITIONS}
                  add={add}
                  buttonLabel={`Add ${label}`}
                />
              </>
            )}
          </Form.List>
        </Card>
      ),
    })),
  ];

  const ready = !editMode || (editMode && !fetching && !!plan);

  return (
    <RobinPage title={editMode ? t("title.edit_plan") : t("title.add_plan")}>
      {contextHolder}
      {ready ? (
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={[16, 16]}>
            <Col md={8} sm={24} xs={24}>
              <Form.Item
                name="name"
                label={t("label.plan.name")}
                rules={[
                  {
                    required: true,
                    message: t("message.validation.required.plan_name"),
                  },
                  { max: 70, message: t("message.error.max_length_70") },
                  {
                    pattern: /^[\u0600-\u06FFa-zA-Z0-9\s-_]+$/,
                    message: t("message.error.invalid_characters"),
                  },
                ]}
              >
                <Input maxLength={70} />
              </Form.Item>
            </Col>
            <Col md={8} sm={24} xs={24}>
              <Form.Item
                name="commissionRate"
                label={t("label.plan.commission_rate")}
                rules={[
                  {
                    required: true,
                    message: t("message.validation.required.commission_rate"),
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  max={100}
                  precision={0}
                  addonAfter={"%"}
                  style={{ width: "100%" }}
                  parser={parseArabicNumbers}
                />
              </Form.Item>
            </Col>
            <Col md={8} sm={24} xs={24}>
              <Form.Item
                name="minCommissionAmount"
                label={t("label.plan.min_commission_amount")}
                rules={[
                  {
                    required: true,
                    message: t(
                      "message.validation.required.min_commission_amount",
                    ),
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  max={100000}
                  precision={0}
                  addonAfter={t("label.currency")}
                  style={{ width: "100%" }}
                  parser={parseArabicNumbers}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Tabs
                defaultActiveKey="1"
                type="card"
                size="large"
                items={items}
                tabBarStyle={{ marginBottom: 24, fontWeight: 500 }}
                style={{
                  background: token.colorBgLayout,
                  padding: "16px",
                  borderRadius: "8px",
                }}
              />
            </Col>
            <Col span={24}>
              <Form.Item>
                <Space>
                  <Button onClick={() => navigate(-1)}>
                    {t("button.cancel")}
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={creating || updating}
                  >
                    {t("button.save")}
                  </Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      ) : (
        <Spin />
      )}
    </RobinPage>
  );
}

export default PlanMutate;
